import Phaser from 'phaser';
import { COLORS } from '../constants/colors';
import { getEntityScale } from '../constants/scale';
import type { EnemyConfig } from './EnemyConfig';
import type { MovementBehavior } from './behaviors/MovementBehavior';
import { createBehavior } from './behaviors/registry';
import { recordEnemyPuddle } from '../systems/puddles';

const KNOCKBACK_DISTANCE = 50;
const KNOCKBACK_DURATION_MS = 150;
const FLASH_DURATION_MS = 50;
const INVULN_FLASH_DURATION_MS = 40;
const INVULN_DEFAULT_COLOR = 0x4444aa;
const HP_TINT_TO = 0xff3333;

// Множитель высоты спрайта над config.size × entityScale. Стартовое значение
// для зайца — 2.0; калибруется визуально на тесте. Хитбокс/физика остаются
// привязаны к config.size — спрайт только визуально шире/выше.
const SPRITE_HEIGHT_MULTIPLIER = 2.0;
// Конвенция: texture key = `enemy_<id>`, анимация = `<id>_run`. Источник истины
// — BootScene.preload/create. Если для id нет текстуры — Enemy остаётся
// чистым Rectangle-плейсхолдером (как все враги до W13).
const SPRITE_TEXTURE_KEY_PREFIX = 'enemy_';
const SPRITE_ANIM_KEY_BY_ID: Record<string, string> = {
  rabbit: 'rabbit_run',
  mouse: 'mouse_run',
  boarling: 'boarling_run',
  fox: 'fox_run',
  beetle: 'beetle_run',
  deer: 'deer_run',
  kangaroo: 'kangaroo_run',
  kangaroo_joey: 'kangaroo_joey_run',
  wolf: 'wolf_run',
  big_boar: 'big_boar_run',
  elk: 'elk_run',
  honey_badger: 'honey_badger_run',
  camel: 'camel_run',
  bear: 'bear_run',
};

export class Enemy extends Phaser.GameObjects.Rectangle {
  declare body: Phaser.Physics.Arcade.Body;

  public readonly config: EnemyConfig;
  public currentHp: number;
  public readonly maxHp: number;
  public isInvulnerable = false;
  // True для врагов, заспавненных через spawnsOnDeath (например, кенгурёнок).
  // Такие враги не учитываются в wave-counter, но дают очки/decrement defense как обычные.
  public readonly isSpawnOnDeathChild: boolean;
  // True для врагов, заспавненных WaveEvent-боссом (Волна Лис, Волна Волков).
  // Дают очки и снимают defense как обычно, но засчитываются в счётчик wave event, не в обычный wave-counter.
  public isWaveEventEnemy = false;
  protected readonly behavior: MovementBehavior;
  protected readonly baseColor: number;
  protected combatColor: number;
  // Спрайт-overlay поверх прозрачного Rectangle. Создаётся только если для
  // id загружена текстура enemy_<id>. Хитбокс/физика остаются на Rectangle —
  // спрайт лишь визуальная маска поверх той же позиции.
  protected sprite?: Phaser.GameObjects.Sprite;
  // Запомненные ключи bench-текстуры и анимации бега — чтобы вернуться к ним
  // после смены на shellTextureKey в фазе invulnerable (beetle).
  private spriteRunTextureKey?: string;
  // Protected — Boss использует ключ анимации бега в applyPhaseSpriteAnim
  // (play(key, true) на входе в charge-фазу).
  protected spriteRunAnimKey?: string;
  // Tween «дыхания» в фазе закрытого панциря. Останавливается в open-фазе и в
  // DESTROY hook'е, чтобы не висел на уничтоженном sprite.
  private shellWobbleTween?: Phaser.Tweens.Tween;
  private isDying = false;
  private flashTimer?: Phaser.Time.TimerEvent;
  private invulnElapsedMs = 0;
  private invulnPhase: 'open' | 'closed' = 'open';

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    config: EnemyConfig,
    isSpawnOnDeathChild = false,
  ) {
    super(scene, x, y, config.size, config.size, config.color);
    this.config = config;
    this.maxHp = config.maxHp;
    this.currentHp = config.maxHp;
    this.isSpawnOnDeathChild = isSpawnOnDeathChild;
    this.baseColor = config.color;
    this.combatColor = config.color;

    scene.add.existing(this);
    scene.physics.add.existing(this);
    // Behavior двигает x/y вручную, физический body сам синхронизируется.
    this.body.setVelocity(0, 0);

    // Адаптивный масштаб врагов от высоты экрана. config.size — базовый размер
    // (= размер на 720). На 360 setScale 0.55 → визуально вдвое меньше + hitbox
    // тоже (Arcade body учитывает displaySize). Behaviors используют displayWidth
    // вместо config.size для корректного clamp в границы поля при масштабе.
    this.setScale(getEntityScale(scene));

    // Спрайт-overlay для врагов, у которых загружена текстура enemy_<id>.
    // Rectangle становится глобально прозрачным (физика и позиция не трогаются —
    // только визуал скрывается). Спрайт уничтожается вместе с Enemy через
    // DESTROY event — не нужно вручную дёргать destroy в каждом месте.
    const spriteKey = SPRITE_TEXTURE_KEY_PREFIX + config.id;
    if (scene.textures.exists(spriteKey)) {
      this.sprite = scene.add.sprite(x, y, spriteKey);
      // Origin до setDisplaySize — не влияет на размер, но определяет точку
      // привязки. (0.5, 1) для honey_badger: "ноги" в точке (sprite.x, sprite.y).
      if (config.spriteOrigin) {
        this.sprite.setOrigin(config.spriteOrigin[0], config.spriteOrigin[1]);
      }
      const animKey = SPRITE_ANIM_KEY_BY_ID[config.id];
      if (animKey && scene.anims.exists(animKey)) {
        this.sprite.play(animKey);
      }
      // Запоминаем run-ключи — для возврата в open-фазе после shell.
      this.spriteRunTextureKey = spriteKey;
      this.spriteRunAnimKey = animKey;
      this.resizeSpriteToConfig();
      // Заяц (и спрайтовые враги в целом) движутся вправо — спрайтлист уже
      // ориентирован так. Если в будущем заведём врагов, идущих влево, тут
      // будет setFlipX(true).
      this.setAlpha(0);
      this.once(Phaser.GameObjects.Events.DESTROY, () => {
        this.shellWobbleTween?.stop();
        this.sprite?.destroy();
      });
    }

    this.behavior = createBehavior(config.movementBehavior);
    this.behavior.init(this, config.movementParams);
  }

  // Двигает врага через текущий MovementBehavior. Не двигаем мёртвых.
  tick(deltaMs: number, target: { x: number; y: number }): void {
    if (this.isDying) return;
    this.tickInvulnerability(deltaMs);
    this.behavior.update(this, deltaMs, target);
    this.syncSpriteToEnemyPosition();
  }

  // Sync overlay-спрайта к позиции Rectangle (behavior двигает x/y).
  // Для origin (0.5, 0.5) — offset 0, sprite.y = this.y (big_boar/elk).
  // Для origin (0.5, 1) — offset +halfTargetH, чтобы низ спрайта совпал с
  // нижним краем хитбокс-Rectangle (honey_badger). targetH из конфига —
  // стабильно при squash-tween (не зависит от текущего displayHeight).
  private syncSpriteToEnemyPosition(): void {
    if (!this.sprite) return;
    this.sprite.x = this.x;
    const targetH = this.config.size * getEntityScale(this.scene) * SPRITE_HEIGHT_MULTIPLIER;
    this.sprite.y = this.y + (this.sprite.originY - 0.5) * targetH;
  }

  private tickInvulnerability(deltaMs: number): void {
    const cycle = this.config.invulnerabilityCycle;
    if (!cycle) return;
    this.invulnElapsedMs += deltaMs;

    if (this.invulnPhase === 'open' && this.invulnElapsedMs >= cycle.openMs) {
      this.invulnPhase = 'closed';
      this.invulnElapsedMs = 0;
      this.isInvulnerable = true;
      this.applyInvulnerabilityVisual();
    } else if (this.invulnPhase === 'closed' && this.invulnElapsedMs >= cycle.closedMs) {
      this.invulnPhase = 'open';
      this.invulnElapsedMs = 0;
      this.isInvulnerable = false;
      this.removeInvulnerabilityVisual();
    }
  }

  private applyInvulnerabilityVisual(): void {
    const color = this.config.invulnerabilityColor ?? INVULN_DEFAULT_COLOR;
    this.combatColor = color;
    this.setFillStyle(color);
    // Спрайт-врагу с shellTextureKey — стоп анимация, переключение на статичную
    // shell-текстуру + лёгкое покачивание («дышит» под панцирем). Размер
    // пересчитывается под новый aspect ratio shell-текстуры.
    const shellKey = this.config.shellTextureKey;
    if (this.sprite && shellKey && this.scene.textures.exists(shellKey)) {
      this.sprite.stop();
      this.sprite.setTexture(shellKey);
      this.resizeSpriteToConfig();
      // Опциональный shell-shrink (жук съёживается под панцирь). Применяется
      // ПОСЛЕ resizeSpriteToConfig, поэтому новые displayWidth/Height
      // умножаются на shellScale без двойного эффекта.
      const shellScale = this.config.shellScale ?? 1.0;
      if (shellScale !== 1.0) {
        this.sprite.setDisplaySize(
          this.sprite.displayWidth * shellScale,
          this.sprite.displayHeight * shellScale,
        );
      }
      this.shellWobbleTween?.stop();
      this.shellWobbleTween = this.scene.tweens.add({
        targets: this.sprite,
        angle: { from: -2.5, to: 2.5 },
        duration: 600,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
      });
    }
  }

  private removeInvulnerabilityVisual(): void {
    this.combatColor = this.baseColor;
    this.setFillStyle(this.baseColor);
    if (this.maxHp > 1) this.updateColorByHp();
    // Возврат к анимации бега для спрайт-врага. Без shellTextureKey в конфиге
    // sprite остаётся на run-анимации с самого начала — этот блок ничего не делает.
    if (this.sprite && this.config.shellTextureKey && this.spriteRunTextureKey) {
      this.shellWobbleTween?.stop();
      this.shellWobbleTween = undefined;
      this.sprite.setAngle(0);
      this.sprite.setTexture(this.spriteRunTextureKey);
      this.resizeSpriteToConfig();
      if (this.spriteRunAnimKey && this.scene.anims.exists(this.spriteRunAnimKey)) {
        this.sprite.play(this.spriteRunAnimKey);
      }
    }
  }

  // Целевая высота спрайт-overlay'а = config.size × entityScale × K. Ширина
  // считается из aspect ratio ТЕКУЩЕЙ текстуры sprite — то есть метод можно
  // вызывать после setTexture (когда aspect меняется на shell vs run).
  private resizeSpriteToConfig(): void {
    if (!this.sprite) return;
    const aspect = this.sprite.width / this.sprite.height;
    const targetH = this.config.size * getEntityScale(this.scene) * SPRITE_HEIGHT_MULTIPLIER;
    this.sprite.setDisplaySize(targetH * aspect, targetH);
  }

  // Возвращает true если умер от этого урона.
  damage(amount: number): boolean {
    if (this.isDying) return false;
    if (this.isInvulnerable) {
      this.triggerInvulnerableHitFlash();
      return false;
    }
    const armor = this.config.armor ?? 0;
    const effective = Math.max(1, amount - armor);
    this.currentHp -= effective;
    this.flash();
    if (this.maxHp > 1) this.updateColorByHp();
    // Олень и любой другой враг со speedHpMultiplier: синхронизируем темп
    // анимации бега с потерей hp. timeScale 1.0 → ~1.625 при 0% hp = 8→13 fps,
    // копыта зачастят в такт ускорению ChaseBehavior'a. Привязано к
    // speedHpMultiplier-флагу, чтобы не трогать остальных спрайт-врагов.
    if (
      this.sprite &&
      this.config.speedHpMultiplier &&
      this.maxHp > 1 &&
      this.sprite.anims.currentAnim
    ) {
      const lostRatio = 1 - this.currentHp / this.maxHp;
      this.sprite.anims.timeScale = 1 + lostRatio * 0.625;
    }
    return this.currentHp <= 0;
  }

  private triggerInvulnerableHitFlash(): void {
    this.flashTimer?.remove(false);
    this.setFillStyle(0xffffff);
    this.flashTimer = this.scene.time.delayedCall(INVULN_FLASH_DURATION_MS, () => {
      if (this.isDying) return;
      this.setFillStyle(this.combatColor);
    });
  }

  // TODO (W11/W13): при переходе на спрайтовых врагов заменить setFillStyle на setTint
  // в flash() и updateColorByHp(). Phaser.Rectangle не поддерживает tint, но текстурный
  // спрайт поддерживает — позволит сохранить базовый цвет/спрайт под белой/красной маской.

  // Совместимость с GameScene: вызывается на момент попадания (визуальный шлёп).
  flash(): void {
    this.flashTimer?.remove(false);
    this.setFillStyle(COLORS.white);
    this.flashTimer = this.scene.time.delayedCall(FLASH_DURATION_MS, () => {
      this.setFillStyle(this.combatColor);
    });
  }

  private updateColorByHp(): void {
    if (this.maxHp <= 1) return;
    const lostRatio = 1 - this.currentHp / this.maxHp;
    const color = Phaser.Display.Color.Interpolate.ColorWithColor(
      Phaser.Display.Color.IntegerToColor(this.baseColor),
      Phaser.Display.Color.IntegerToColor(HP_TINT_TO),
      100,
      Math.floor(lostRatio * 100),
    );
    this.combatColor = Phaser.Display.Color.GetColor(color.r, color.g, color.b);
    this.setFillStyle(this.combatColor);
  }

  // Совместимость: для текущей логики hit zone.
  // Порог 22 — после двух итераций редукции (Волна 1: ×0.75, докрутка: ×0.85,
  // итого размер ×0.6375 от исходных) порог опущен пропорционально (35 → 22).
  // Сохраняет ту же группу врагов «хедшотабельной»:
  // мелкие 1-hp (Мышь 14, Заяц 20, Жук 18, Кенгурёнок 16) — без headshot,
  // Кабанчик 27 / Лиса 27 / Волк 31 / Кенгуру 38 / Олень 45 — с headshot.
  getHitZone(bulletY: number): 'head' | 'body' {
    if (this.config.size <= 22) return 'body';
    const headTopY = this.y - this.config.size / 2;
    const headBottomY = this.y - this.config.size / 6;
    if (bulletY >= headTopY && bulletY <= headBottomY) return 'head';
    return 'body';
  }

  getIsDying(): boolean {
    return this.isDying;
  }

  private handleSpawnOnDeath(): void {
    const sod = this.config.spawnsOnDeath;
    if (!sod) return;
    if (Math.random() >= sod.chance) return;
    this.scene.events.emit('enemy:spawn-on-death', {
      id: sod.id,
      x: this.x,
      y: this.y,
    });
  }

  dieWithKnockback(onComplete: () => void): void {
    if (this.isDying) return;
    this.isDying = true;
    this.handleSpawnOnDeath();

    // Декаль-лужа на месте смерти врага. Накапливается между матчами в пределах
    // вкладки браузера (см. systems/puddles). Логика боя не задействована —
    // лужа чисто визуальная, без коллизий и физики. Если для id нет массива
    // текстур в PUDDLES_BY_ENEMY — лужа не спавнится.
    recordEnemyPuddle(this.config.id, this.scene, this.x, this.y, this.config.size);

    this.body.setVelocity(0, 0);

    // Спрайт-overlay (если есть) ноtw'ится параллельно: те же x и alpha.
    // Sync перед tween — на случай, если tick() не успел в этом кадре.
    this.syncSpriteToEnemyPosition();
    this.scene.tweens.add({
      targets: this.sprite ? [this, this.sprite] : this,
      x: this.x - KNOCKBACK_DISTANCE,
      alpha: 0,
      duration: KNOCKBACK_DURATION_MS,
      ease: 'Cubic.easeOut',
      onComplete: () => {
        onComplete();
        // Sprite уничтожается автоматически через DESTROY hook из конструктора.
        this.destroy();
      },
    });
  }
}
